import type { SettingGroup } from './settingsCatalog.js';

/**
 * RECORDING ATTENDANCE — the settings group, extracted.
 *
 * How a punch happens and when the server refuses one. The location and face
 * sections carry the `pay` flag because a rejected punch is unpaid time.
 *
 * Extracted from `settingsCatalog.ts` for the same reason `SECURITY_GROUP`
 * was: that file is over its line budget and may only shrink, and this is the
 * group whose rows each need a paragraph — three of them exist to record that
 * a control does something NARROWER than its neighbours imply, and a
 * paragraph deleted for space is the fastest route back to the drift it
 * describes.
 *
 * The type import is type-only, so the cycle back to `settingsCatalog.ts`
 * exists at compile time and not at runtime. The three option tables below moved
 * across with the group: no other group referenced them, and leaving them
 * behind would have turned this file's type-only import into a VALUE cycle
 * that fails at module-evaluation time rather than at compile time.
 */
export const MOCK_LOCATION_OPTIONS = [
  { value: 'reject', labelKey: 'settings.opt.mock.reject' },
  { value: 'flag', labelKey: 'settings.opt.mock.flag' },
  { value: 'allow', labelKey: 'settings.opt.mock.allow' },
] as const;

/** WHO the web face match reaches, once it is switched on. Keyed on the same
 *  workforceType split the rest of the app uses for the two populations. */
export const FACE_WEB_SCOPE_OPTIONS = [
  { value: 'everyone', labelKey: 'settings.opt.faceWebScope.everyone' },
  { value: 'site_workers', labelKey: 'settings.opt.faceWebScope.siteWorkers' },
  { value: 'office_staff', labelKey: 'settings.opt.faceWebScope.officeStaff' },
] as const;

export const FIELD_REVIEW_OPTIONS = [
  { value: 'auto_count', labelKey: 'settings.opt.review.autoCount' },
  { value: 'verify', labelKey: 'settings.opt.review.verify' },
] as const;

export const RECORDING_GROUP: SettingGroup = {
  id: 'recording',
  sections: [
    {
      id: 'recording.methods',
      fields: [
        {
          id: 'attendance.modeAssigned',
          category: 'attendance',
          path: 'accessModes.assignedWorksite',
          kind: 'toggle',
        },
        {
          id: 'attendance.modeGeofence',
          category: 'attendance',
          path: 'accessModes.geofence',
          kind: 'toggle',
        },
        // THE FOURTH FLAG, AND WHY ITS ABSENCE MADE THE THIRD ONE DEAD.
        //
        // `accessModes` has four members and this section offered three.
        // `kiosk` was therefore permanently `true` — its schema default,
        // unreachable by any admin — and `profileOptionsFor`
        // (routes/employees/attendanceProfileOptions.ts) ORs a profile's
        // `offeredKeys`. Both profiles that name `app` also name `kiosk`
        // (SELF_ANY_SITE, FACE_DEVICE), so a permanently-true `kiosk`
        // satisfied the OR on its own and unticking "Field app" changed
        // nothing whatsoever. A toggle that saves, validates, persists and
        // cannot alter one option in one picker is the exact defect this
        // catalogue is audited for. Restoring the flag restores the other.
        {
          id: 'attendance.modeKiosk',
          category: 'attendance',
          path: 'accessModes.kiosk',
          kind: 'toggle',
        },
        {
          id: 'attendance.modeApp',
          category: 'attendance',
          path: 'accessModes.app',
          kind: 'toggle',
        },
      ],
    },
    {
      id: 'recording.sources',
      fields: [
        {
          id: 'attendance.sourceWeb',
          category: 'attendance',
          path: 'sources.web',
          kind: 'toggle',
          pay: true,
        },
        {
          id: 'attendance.manualEntry',
          category: 'attendance',
          path: 'allowManualEntry',
          kind: 'toggle',
          pay: true,
        },
      ],
    },
    {
      id: 'recording.photo',
      fields: [
        {
          id: 'attendance.photoWeb',
          category: 'attendance',
          path: 'requirePhoto.web',
          kind: 'toggle',
        },
        {
          id: 'attendance.photoExemptRemote',
          category: 'attendance',
          path: 'requirePhoto.exemptRemote',
          kind: 'toggle',
          enabledBy: 'attendance.photoWeb',
        },
        // How long the photo survives. It used to sit under "Formats & data
        // retention", two groups away from the switch that decides whether a
        // photo is taken at all — so the question "do we hold biometric
        // images, and for how long" could only be answered by reading two
        // separate screens and remembering both.
        {
          id: 'attendance.photoRetention',
          category: 'attendance',
          path: 'auditPhotoRetentionDays',
          kind: 'number',
          min: 0,
          max: 3650,
          step: 30,
          unitKey: 'settings.unit.days',
        },
      ],
    },
    {
      id: 'recording.location',
      pay: true,
      fields: [
        {
          id: 'attendance.gpsAccuracy',
          category: 'attendance',
          path: 'gpsAccuracyThresholdMeters',
          kind: 'number',
          pay: true,
          min: 10,
          max: 500,
          step: 10,
          unitKey: 'settings.unit.metres',
        },
        {
          id: 'attendance.mockMode',
          category: 'attendance',
          path: 'mockLocationMode',
          kind: 'select',
          pay: true,
          options: MOCK_LOCATION_OPTIONS,
        },
        {
          id: 'attendance.minInterval',
          category: 'attendance',
          path: 'minPunchIntervalSeconds',
          kind: 'number',
          pay: true,
          min: 0,
          max: 600,
          step: 5,
          unitKey: 'settings.unit.seconds',
        },
        {
          id: 'attendance.clockSkew',
          category: 'attendance',
          path: 'serverClientTimeDeltaMaxSeconds',
          kind: 'number',
          pay: true,
          min: 30,
          max: 900,
          step: 30,
          unitKey: 'settings.unit.seconds',
        },
      ],
    },
    {
      id: 'recording.face',
      pay: true,
      fields: [
        {
          id: 'face.enabled',
          category: 'faceRecognition',
          path: 'enabled',
          kind: 'toggle',
          pay: true,
        },
        // NO `enabledBy` ON THE NEXT THREE, and it is the same mistake
        // `face.useCaptureAsAvatar` carries a note about further down.
        //
        // `faceRecognition.enabled` is read in exactly two places in the
        // API — `attendance.service.ts:610` and its preflight at `:1272` —
        // and both are the WEB self check-in face match. The FIELD/kiosk
        // device policy is assembled by `resolveFacePolicy`
        // (`packages/api/src/services/field.shared.ts:109-129`) and pushed
        // down on `/field/sync`; it reads `minConfidence`,
        // `fieldReviewMode` and `fieldReverifyMargin` and never looks at
        // `enabled` at all. So these three keep governing every handset scan
        // in the company while `enabled` is off — which is the SHIPPED
        // DEFAULT (`enabled: z.boolean().default(false)`). Greying them out
        // meant the tenants actually running face scans could not set the
        // match threshold their devices were using. §3.11 is about naming a
        // real obstacle; this one was invented.
        {
          id: 'face.minConfidence',
          category: 'faceRecognition',
          path: 'minConfidence',
          kind: 'number',
          pay: true,
          min: 0,
          max: 1,
          step: 0.05,
        },
        {
          id: 'face.fieldReviewMode',
          category: 'faceRecognition',
          path: 'fieldReviewMode',
          kind: 'select',
          pay: true,
          options: FIELD_REVIEW_OPTIONS,
        },
        {
          id: 'face.fieldReverifyMargin',
          category: 'faceRecognition',
          path: 'fieldReverifyMargin',
          kind: 'number',
          pay: true,
          min: 0,
          max: 1,
          step: 0.01,
        },
        {
          id: 'face.verifyOnWeb',
          category: 'faceRecognition',
          path: 'verifyOnWeb',
          kind: 'toggle',
          pay: true,
          enabledBy: 'face.enabled',
        },
        // The switch above says WHETHER; this says WHOM. Directly under it,
        // because reading either alone tells you half the rule.
        {
          id: 'face.webScope',
          category: 'faceRecognition',
          path: 'webScope',
          kind: 'select',
          pay: true,
          options: FACE_WEB_SCOPE_OPTIONS,
          enabledBy: 'face.verifyOnWeb',
        },
        // NO `enabledBy`, and that is the point of this note.
        //
        // It carried `enabledBy: 'face.enabled'`, which greyed the control
        // out for exactly the tenants it exists to serve. The photos this
        // uses are punch captures (`AttendanceEvent.photoUrl`), written by
        // the field and kiosk APKs on every scan whether or not anything is
        // MATCHED against them — face recognition governs the matching, not
        // the camera. So a site running the APK with matching off has the
        // pictures and had no way to switch this on, while its workers
        // showed a blank avatar. `resolveEmployeeAvatarPhoto`
        // (`packages/api/src/routes/employees.routes.ts`) reads this setting
        // alone now, so the control and the behaviour agree.
        //
        // It sits in this section anyway: the photos are face captures and
        // an admin looking for anything about stored faces looks here.
        {
          id: 'face.useCaptureAsAvatar',
          category: 'faceRecognition',
          path: 'useCaptureAsAvatar',
          kind: 'toggle',
        },
      ],
    },
  ],
};
